import React, { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowUp,
  BadgePercent,
  BarChart3,
  CalendarDays,
  CreditCard,
  Eye,
  PieChart as PieChartIcon,
  Shield,
  TrendingUp,
  UserX,
  Users,
  X,
} from 'lucide-react';
import adminService from '../services/adminService';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Bar,
  LabelList,
} from 'recharts';

const COLORS = {
  emerald: '#10b981',
  red: '#D91F26',
  orange: '#f97316',
  indigo: '#6366f1',
  blue: '#0ea5e9',
  purple: '#7c3aed',
};

const ChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-[#E2E8F0] bg-white p-3 shadow-lg">
        <p className="text-xs font-medium text-[#94A3B8]">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} className="text-sm font-bold text-[#0F172A]">
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function pct(value, total) {
  if (!total || total === 0) return '0%';
  return ((value / total) * 100).toFixed(1) + '%';
}

export default function Dashboard({ onNavigateToUser, section = 'dashboard', stats }) {
  const [drawer, setDrawer] = useState(null);
  const [reminderState, setReminderState] = useState({
    loading: false,
    message: '',
    error: '',
  });

  if (!stats) {
    return (
      <div className="rounded-lg border border-[#E2E8F0] bg-white p-10 text-sm font-medium text-[#94A3B8] shadow-sm">
        Loading live analytics...
      </div>
    );
  }

  const recentReports = stats.recentReports || [];
  const growth = stats.analytics?.userGrowth || [];
  const flaggedUsers = stats.risk?.flaggedUsers || [];
  const subscriptionBreakdown = stats.subscriptions?.breakdown || [];

  if (section !== 'dashboard') {
    return (
      <InsightsPage
        flaggedUsers={flaggedUsers}
        mode={section}
        onNavigateToUser={onNavigateToUser}
        recentReports={recentReports}
        stats={stats}
        subscriptionBreakdown={subscriptionBreakdown}
      />
    );
  }

  const total = stats.users?.total ?? 0;
  const active = stats.users?.active ?? 0;
  const banned = stats.users?.banned ?? 0;
  const newToday = stats.users?.newToday ?? 0;
  const newThisMonth = stats.users?.newThisMonth ?? 0;
  const subscribers = stats.subscriptions?.active ?? 0;
  const activeRate = pct(active, total);
  const bannedRate = pct(banned, total);
  const subRate = pct(subscribers, total);
  const reports = stats.reports ?? {};
  const reportResolved = reports.resolved ?? 0;
  const reportOpen = reports.open ?? 0;
  const reportTotal = reports.total || reportOpen + reportResolved;
  const resolutionRate = reportTotal > 0 ? ((reportResolved / reportTotal) * 100).toFixed(1) + '%' : '0%';
  const monthGrowthRate = pct(newThisMonth, total);
  const authMethods = stats.users?.authMethods ?? {};
  const otpTotal = (authMethods.otp || 0) + (authMethods.email || 0);
  const otpRate = pct(authMethods.otp || 0, otpTotal);

  const sendSubscriptionReminders = async () => {
    setReminderState({ loading: true, message: '', error: '' });
    try {
      const result = await adminService.sendSubscriptionReminders();
      setReminderState({
        loading: false,
        error: '',
        message: result.queued
          ? `Queued reminders for ${result.targetedUsers || 0} users. Delivery will continue in the background.`
          : `Sent to ${result.targetedUsers || 0} users: ${result.pushSent || 0} push, ${result.emailSent || 0} email.`,
      });
    } catch (error) {
      setReminderState({
        loading: false,
        message: '',
        error: error.message || 'Could not send subscription reminders.',
      });
    }
  };

  return (
    <div className="space-y-6">
      <SummaryBar
        items={[
          { label: 'Active Rate', value: activeRate, icon: <Activity size={14} />, color: 'text-emerald-600' },
          { label: 'Banned', value: bannedRate, icon: <UserX size={14} />, color: 'text-[#D91F26]' },
          { label: 'Subscribers', value: subRate, icon: <CreditCard size={14} />, color: 'text-indigo-600' },
          { label: 'Resolved', value: resolutionRate, icon: <Shield size={14} />, color: 'text-emerald-600' },
          { label: 'Growth', value: monthGrowthRate, icon: <TrendingUp size={14} />, color: 'text-blue-600' },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          accentColor="#0F172A"
          icon={<Users size={16} />}
          label="Total Users"
          value={total}
          sub={newThisMonth > 0 ? `+${newThisMonth} this month` : undefined}
          trend={newThisMonth > 0 ? 'up' : undefined}
        />
        <MetricCard
          accentColor="#10b981"
          icon={<Activity size={16} />}
          label="Active"
          value={active}
          sub={activeRate}
          color="text-emerald-600"
          progress={active / Math.max(total, 1)}
        />
        <MetricCard
          accentColor="#D91F26"
          icon={<AlertTriangle size={16} />}
          label="Banned"
          value={banned}
          sub={bannedRate}
          color="text-[#D91F26]"
          progress={banned / Math.max(total, 1)}
        />
        <MetricCard
          accentColor="#0ea5e9"
          icon={<CalendarDays size={16} />}
          label="New Today"
          value={newToday}
          trend={newToday > 0 ? 'up' : undefined}
        />
        <MetricCard
          accentColor="#6366f1"
          icon={<CreditCard size={16} />}
          label="Subscribers"
          value={subscribers}
          sub={subRate}
          color="text-indigo-600"
          progress={subscribers / Math.max(total, 1)}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr_0.8fr]">
        <Panel
          eyebrow="Registration Analytics"
          title="New users over the last 14 days"
          action={<TrendingUp size={20} className="text-emerald-500" />}
        >
          <RegistrationChart data={growth} />
        </Panel>

        <Panel eyebrow="Quick Review" title="Admin actions" action={<Eye size={20} className="text-[#94A3B8]" />}>
          <div className="space-y-3">
            <ReviewButton
              color="text-[#D91F26]"
              icon={<AlertTriangle size={18} />}
              label="Reports"
              meta={`${reportOpen} open`}
              onClick={() => setDrawer('reports')}
            />
            <ReviewButton
              color="text-orange-500"
              icon={<Shield size={18} />}
              label="Risk monitor"
              meta={`${flaggedUsers.length} flagged`}
              onClick={() => setDrawer('risk')}
            />
            <ReviewButton
              color="text-indigo-600"
              icon={<BarChart3 size={18} />}
              label="More analytics"
              meta="Composition and payments"
              onClick={() => setDrawer('analytics')}
            />
            <ReviewButton
              color="text-emerald-600"
              icon={<BadgePercent size={18} />}
              label={reminderState.loading ? 'Sending reminders' : 'Send reminders'}
              meta="Push OTP users, email real emails"
              onClick={sendSubscriptionReminders}
            />
            {(reminderState.message || reminderState.error) && (
              <p className={`rounded-lg border px-3 py-2 text-xs font-semibold ${
                reminderState.error
                  ? 'border-red-100 bg-red-50 text-[#D91F26]'
                  : 'border-emerald-100 bg-emerald-50 text-emerald-700'
              }`}>
                {reminderState.error || reminderState.message}
              </p>
            )}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <InsightCard
          accentColor="#0ea5e9"
          icon={<CalendarDays size={18} />}
          label="New This Month"
          value={newThisMonth}
          sub={`${monthGrowthRate} of total users`}
        />
        <InsightCard
          accentColor="#10b981"
          icon={<Shield size={18} />}
          label="Reports Resolved"
          value={reportResolved}
          sub={`${resolutionRate} resolution rate`}
        />
        <InsightCard
          accentColor="#0ea5e9"
          icon={<PieChartIcon size={18} />}
          label="OTP Signups"
          value={authMethods.otp ?? 0}
          sub={`${otpRate} of authenticated users`}
        />
        <InsightCard
          accentColor="#6366f1"
          icon={<BadgePercent size={18} />}
          label="Conversion"
          value={subRate}
          sub={`${subscribers}/${total} users subscribed`}
        />
      </div>

      {drawer && (
        <InsightsDrawer
          flaggedUsers={flaggedUsers}
          mode={drawer}
          onClose={() => setDrawer(null)}
          onNavigateToUser={onNavigateToUser}
          recentReports={recentReports}
          stats={stats}
          subscriptionBreakdown={subscriptionBreakdown}
        />
      )}
    </div>
  );
}

function SummaryBar({ items }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && (
            <span className="hidden h-6 w-px bg-[#E2E8F0] sm:block" />
          )}
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider">
            <span className="flex items-center gap-1.5 rounded-lg bg-[#F8FAFC] px-3 py-2">
              <span className={item.color}>{item.icon}</span>
              <span className="text-[#94A3B8]">{item.label}</span>
              <span className={`${item.color} font-semibold`}>{item.value}</span>
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function InsightsDrawer({
  flaggedUsers,
  mode,
  onClose,
  onNavigateToUser,
  recentReports,
  stats,
  subscriptionBreakdown,
}) {
  const authBreakdown = [
    { label: 'OTP', value: stats.users?.authMethods?.otp ?? 0, color: COLORS.blue },
    { label: 'Email', value: stats.users?.authMethods?.email ?? 0, color: COLORS.purple },
  ];
  const title =
    mode === 'reports'
      ? 'Reports'
      : mode === 'risk'
      ? 'Risk Monitor'
      : 'Analytics';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E2E8F0] bg-white/95 p-6 backdrop-blur">
          <div>
            <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
              Dashboard Drawer
            </p>
            <h2 className="text-lg font-bold text-[#0F172A]">{title}</h2>
          </div>
          <button className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#F1F5F9] transition-colors" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <InsightsContent
            authBreakdown={authBreakdown}
            flaggedUsers={flaggedUsers}
            mode={mode}
            onNavigateToUser={onNavigateToUser}
            recentReports={recentReports}
            stats={stats}
            subscriptionBreakdown={subscriptionBreakdown}
          />
        </div>
      </div>
    </div>
  );
}

function InsightsPage({
  flaggedUsers,
  mode,
  onNavigateToUser,
  recentReports,
  stats,
  subscriptionBreakdown,
}) {
  const authBreakdown = [
    { label: 'OTP', value: stats.users?.authMethods?.otp ?? 0, color: COLORS.blue },
    { label: 'Email', value: stats.users?.authMethods?.email ?? 0, color: COLORS.purple },
  ];
  const title =
    mode === 'reports'
      ? 'Reports'
      : mode === 'risk'
      ? 'Risk Monitor'
      : 'Analytics';

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
          Admin Section
        </p>
        <h2 className="mt-1 text-2xl font-bold text-[#0F172A] tracking-tight">{title}</h2>
      </div>
      <InsightsContent
        authBreakdown={authBreakdown}
        flaggedUsers={flaggedUsers}
        mode={mode}
        onNavigateToUser={onNavigateToUser}
        recentReports={recentReports}
        stats={stats}
        subscriptionBreakdown={subscriptionBreakdown}
      />
    </div>
  );
}

function InsightsContent({
  authBreakdown,
  flaggedUsers,
  mode,
  onNavigateToUser,
  recentReports,
  stats,
  subscriptionBreakdown,
}) {
  if (mode === 'reports') {
    return <ReportsSection onNavigateToUser={onNavigateToUser} reports={recentReports} />;
  }
  if (mode === 'risk') {
    return <RiskSection onNavigateToUser={onNavigateToUser} users={flaggedUsers} />;
  }
  return (
    <>
      <Panel eyebrow="Account Mix" title="System composition" action={<Users size={20} className="text-[#94A3B8]" />}>
        <div className="grid grid-cols-2 gap-4">
          <SmallStat label="Admins" value={stats.users?.admins ?? 0} />
          <SmallStat label="Deactivated" value={stats.users?.deactivated ?? 0} />
          <SmallStat label="This Month" value={stats.users?.newThisMonth ?? 0} />
          <SmallStat label="Open Reports" value={stats.reports?.open ?? 0} />
        </div>
        <div className="mt-5">
          <CompositionChart
            items={[
              { label: 'Active users', value: stats.users?.active ?? 0, color: COLORS.emerald },
              { label: 'Banned users', value: stats.users?.banned ?? 0, color: COLORS.red },
              { label: 'Open reports', value: stats.reports?.open ?? 0, color: COLORS.orange },
              { label: 'Paid subscribers', value: stats.subscriptions?.active ?? 0, color: COLORS.indigo },
            ]}
          />
        </div>
      </Panel>
      <Panel eyebrow="Authentication" title="OTP vs email signups" action={<Shield size={20} className="text-[#94A3B8]" />}>
        <AuthPieChart items={authBreakdown} />
      </Panel>
      <Panel eyebrow="Subscriptions" title="Payment status breakdown" action={<BarChart3 size={20} className="text-[#94A3B8]" />}>
        <CompositionChart
          items={subscriptionBreakdown.map((row) => ({
            label: row.status,
            value: row.total,
            color: row.status === 'ACTIVE' ? COLORS.emerald : '#94a3b8',
          }))}
        />
      </Panel>
    </>
  );
}

function ReportsSection({ onNavigateToUser, reports }) {
  return (
    <Panel eyebrow="Moderation" title="Recent reports" action={<AlertTriangle size={20} className="text-[#94A3B8]" />}>
      <div className="space-y-3">
        {reports.length === 0 ? (
          <EmptyText>No recent reports.</EmptyText>
        ) : (
          reports.map((report) => (
            <div key={report.id} className="rounded-lg border border-[#E2E8F0] p-4 hover:bg-[#F8FAFC] transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#0F172A]">
                    {report.reported?.name || report.reported?.email || 'Reported user'}
                  </p>
                  <p className="mt-1 text-xs font-medium text-[#94A3B8]">
                    Reported by {report.reporter?.name || report.reporter?.email || 'Unknown'}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    {report.reason || report.details || 'No reason provided'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {report.reported?.id && (
                    <button
                      className="rounded-md border border-[#E2E8F0] px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-[#F1F5F9]"
                      onClick={() => onNavigateToUser(report.reported.id)}
                    >
                      Open
                    </button>
                  )}
                  <span className="rounded-full bg-[#F8FAFC] px-2.5 py-0.5 text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wider">
                    {report.status}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

function RiskSection({ onNavigateToUser, users }) {
  return (
    <Panel eyebrow="Risk Monitor" title="Users needing review" action={<Shield size={20} className="text-[#94A3B8]" />}>
      <div className="space-y-3">
        {users.length === 0 ? (
          <EmptyText>No suspicious activity currently flagged.</EmptyText>
        ) : (
          users.map((user) => (
            <div key={user.id} className="rounded-lg border border-[#E2E8F0] p-4 hover:bg-[#F8FAFC] transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#0F172A]">
                    {user.name || user.email || user.phone || user.id}
                  </p>
                  <p className="truncate text-xs font-medium text-[#94A3B8]">
                    {user.email || user.phone || 'No contact'}
                  </p>
                </div>
                <button
                  className="shrink-0 rounded-md bg-red-50 border border-red-100 px-3 py-1 text-xs font-semibold text-[#D91F26] transition hover:bg-red-100"
                  onClick={() => onNavigateToUser(user.id)}
                >
                  Review
                </button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <MiniPill label="Sessions" value={user.activeSessionCount} />
                <MiniPill label="Devices" value={user.activeDeviceCount} />
                <MiniPill label="Reports" value={user.reportsAgainstCount} />
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

function ReviewButton({ color, icon, label, meta, onClick }) {
  return (
    <button
      className="flex w-full items-center justify-between gap-4 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-left transition hover:border-[#CBD5E1] hover:bg-white"
      onClick={onClick}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-[#E2E8F0] ${color}`}>{icon}</span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-[#0F172A]">{label}</span>
          <span className="block truncate text-xs font-medium text-[#94A3B8]">{meta}</span>
        </span>
      </span>
      <span className="text-sm font-semibold text-[#94A3B8]">&gt;</span>
    </button>
  );
}

function Panel({ action, children, eyebrow, title }) {
  return (
    <section className="rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
            {eyebrow}
          </p>
          <h2 className="mt-1 text-base font-semibold text-[#0F172A]">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function RegistrationChart({ data }) {
  if (!data || data.length === 0) return <EmptyText>No growth data yet.</EmptyText>;

  return (
    <div className="overflow-hidden rounded-lg bg-[#F8FAFC] p-3">
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.red} stopOpacity={0.2} />
              <stop offset="95%" stopColor={COLORS.red} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) => v.slice(5)}
            tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }}
            tickLine={false}
            axisLine={false}
            label={{ value: 'Date', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11, fontWeight: 600 }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            label={{ value: 'Users', angle: -90, position: 'insideLeft', offset: 10, fill: '#94a3b8', fontSize: 11, fontWeight: 600 }}
          />
          <Tooltip content={<ChartTooltip />} />
          <Area
            type="monotone"
            dataKey="total"
            stroke={COLORS.red}
            strokeWidth={2}
            fill="url(#growthFill)"
            dot={{ fill: '#fff', stroke: COLORS.red, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: COLORS.red, stroke: '#fff', strokeWidth: 2 }}
          >
            <LabelList
              dataKey="total"
              position="top"
              fill="#64748b"
              fontSize={10}
              fontWeight={600}
              offset={5}
            />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function AuthPieChart({ items }) {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  if (total === 0) return <EmptyText>No authentication data available.</EmptyText>;

  return (
    <div className="grid items-center gap-5 sm:grid-cols-[180px_1fr]">
      <div className="relative mx-auto h-44 w-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={items}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={80}
              dataKey="value"
              paddingAngle={2}
              stroke="none"
            >
              {items.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
          <p className="text-2xl font-bold text-[#0F172A]">{total}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#94A3B8]">Users</p>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between rounded-lg bg-[#F8FAFC] p-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
            <span className="text-sm font-bold text-[#0F172A]">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompositionChart({ items }) {
  if (!items || items.length === 0) return <EmptyText>No data available.</EmptyText>;

  return (
    <ResponsiveContainer width="100%" height={items.length * 56}>
      <RechartsBarChart data={items} layout="vertical" margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
          tickLine={false}
          axisLine={false}
          width={130}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'transparent' }} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
          {items.map((entry, i) => (
            <Cell key={i} fill={entry.color} />
          ))}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

function MetricCard({ accentColor = '#94A3B8', icon, label, value, color = 'text-[#0F172A]', sub, trend, progress }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white shadow-sm overflow-hidden">
      <div className="h-[3px]" style={{ backgroundColor: accentColor }} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}18` }}>
            <span style={{ color: accentColor }}>{icon}</span>
          </div>
        </div>
        <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">{label}</p>
        <p className={`text-2xl font-bold tracking-tight mt-1 ${color}`}>{value}</p>
        {sub && (
          <p className="mt-1 flex items-center gap-1 text-xs font-medium text-[#94A3B8]">
            {trend === 'up' && <ArrowUp size={12} className="text-emerald-500" />}
            {sub}
          </p>
        )}
        {progress !== undefined && (
          <div className="mt-3 h-1.5 rounded-full bg-[#E2E8F0]">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.min(progress * 100, 100)}%`, backgroundColor: accentColor }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function InsightCard({ accentColor = '#94A3B8', icon, label, value, sub }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white shadow-sm p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}18` }}>
          <span style={{ color: accentColor }}>{icon}</span>
        </div>
      </div>
      <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold tracking-tight text-[#0F172A] mt-1">{value}</p>
      {sub && <p className="mt-1 text-xs font-medium text-[#94A3B8]">{sub}</p>}
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4">
      <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
        {label}
      </p>
      <p className="mt-2 text-xl font-bold text-[#0F172A]">{value}</p>
    </div>
  );
}

function MiniPill({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3">
      <p className="text-lg font-bold text-[#0F172A]">{value}</p>
      <p className="text-[10px] font-medium uppercase tracking-wider text-[#94A3B8]">{label}</p>
    </div>
  );
}

function EmptyText({ children }) {
  return (
    <p className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-sm font-medium text-[#94A3B8]">
      {children}
    </p>
  );
}
